/**
 * Application shell.
 *
 * The nav is grouped rather than a flat list: "Operação" and "Administração"
 * are different jobs, and a flat list makes an operator scan settings they will
 * never open. Groups also let the sidebar grow without becoming a wall.
 *
 * The electoral banner is permanent while the restricted window is open — an
 * operator who forgets the window exists is exactly how an improper
 * institutional campaign goes out.
 *
 * ## O menu recolhido
 *
 * Recolher vira uma **régua de ícones**, e não um menu escondido. Sumir com a
 * barra ganharia 72 px a mais e cobraria o preço de a pessoa perder a noção de
 * onde está — a régua mantém o item ativo visível e a navegação a um clique.
 *
 * A largura sai de uma variável CSS em vez de duas classes alternadas: o
 * `<aside>`, o recuo do corpo e o limite de leitura do conteúdo precisam
 * concordar, e três lugares lendo a mesma variável não saem de sincronia como
 * três condicionais saem.
 *
 * Recolher também **alarga o conteúdo**: manter o limite de leitura em 1152 px
 * faria o menu encolher e nada mais acontecer na tela, que é o oposto do
 * pedido. Ele não vira infinito, porém — linha de tabela atravessando um
 * monitor de 34" não se lê.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { api, type EstadoEleitoral } from '../api/cliente'
import { NOMES_PAPEL, podeAdministrar, podeGerirSistema, temVertical, useAuth } from '../estado/auth'
import { MarcaEgba, MarcaProduto } from './marca'
import { formatarDataCurta } from './ui'

const traco = (d: string) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-[18px] shrink-0"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
)

interface Item {
  para: string
  texto: string
  icone: ReactNode
  somenteAdmin?: boolean
  /** Gestão do sistema: só técnico e master (não aparece para a equipe). */
  somenteGestao?: boolean
  /** Só aparece para quem enxerga esta vertical de negócio. */
  vertical?: 'escolas' | 'companhias'
}

interface Grupo {
  titulo: string | null
  itens: Item[]
}

const GRUPOS: Grupo[] = [
  {
    titulo: null,
    itens: [
      { para: '/', texto: 'Visão geral', icone: traco('M3 10.5 12 3l9 7.5M5 9.5V20h14V9.5') },
    ],
  },
  {
    titulo: 'Publicação',
    itens: [
      {
        para: '/pedidos',
        texto: 'Pedidos',
        icone: traco('M9 12.5l2 2 4-4M6 3h12a1 1 0 011 1v16a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z'),
      },
      {
        para: '/instituicoes',
        texto: 'Instituições',
        icone: traco('M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6'),
        somenteAdmin: true,
      },
    ],
  },
  {
    titulo: 'Captação',
    itens: [
      {
        para: '/mercado',
        texto: 'Mercado de escolas',
        vertical: 'escolas',
        icone: traco('M3 3v18h18M7 15l3.5-4 3 3L21 7'),
        somenteAdmin: true,
      },
      {
        para: '/mercado-sa',
        texto: 'Mercado de S.A.',
        vertical: 'companhias',
        icone: traco('M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4M9 11h.01M15 11h.01'),
        somenteAdmin: true,
      },
      {
        para: '/prospeccao',
        texto: 'Prospecção',
        icone: traco('M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35'),
        somenteAdmin: true,
      },
      {
        para: '/landing',
        texto: 'Landing pages',
        icone: traco('M4 5h16v14H4zM4 9h16M9 9v10'),
        somenteAdmin: true,
      },
      {
        para: '/templates',
        texto: 'Templates',
        icone: traco('M4 5h16M4 5v14h16V5M9 5v14M4 10h5'),
        somenteAdmin: true,
      },
      {
        para: '/campanhas',
        texto: 'Campanhas',
        icone: traco('M3 11l18-8-8 18-2-7-8-3z'),
        somenteAdmin: true,
      },
      {
        para: '/empresas',
        texto: 'Empresas',
        icone: traco('M17 20v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9.5 8a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM22 20v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75'),
        somenteAdmin: true,
      },
    ],
  },
  {
    titulo: 'Administração',
    itens: [
      {
        para: '/categorias',
        texto: 'Categorias',
        icone: traco('M4 7h16M4 12h16M4 17h9'),
        somenteAdmin: true,
      },
      {
        para: '/precos',
        texto: 'Tabelas de preço',
        icone: traco('M12 4v16M8.5 8h5.5a2.5 2.5 0 010 5H9.5a2.5 2.5 0 000 5h6'),
        somenteAdmin: true,
      },
      {
        para: '/configuracoes',
        texto: 'Configurações',
          somenteGestao: true,
        icone: traco(
          'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-1.8-.3 1.6 1.6 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.6 1.6 0 008 19.4a1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00.3-1.8 1.6 1.6 0 00-1.5-1H2a2 2 0 110-4h.1A1.6 1.6 0 004.6 8a1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H9a1.6 1.6 0 001-1.5V2a2 2 0 114 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V9a1.6 1.6 0 001.5 1H22a2 2 0 110 4h-.1a1.6 1.6 0 00-1.5 1z',
        ),
        somenteAdmin: true,
      },
    ],
  },
  {
    titulo: 'Pessoas',
    itens: [
      {
        para: '/usuarios',
        texto: 'Usuários e acessos',
        icone: traco('M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM19 8v6M22 11h-6'),
        somenteAdmin: true,
      },
      {
        para: '/apresentacao',
        texto: 'Apresentação',
        icone: traco('M4 4h16v11H4zM8 19h8M12 15v4M9 8l4 2.5L9 13V8z'),
        somenteAdmin: true,
      },
      {
        para: '/perfil',
        texto: 'Meu perfil',
        icone: traco('M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z'),
      },
    ],
  },
  {
    titulo: 'Conformidade',
    itens: [
      {
        para: '/auditoria',
        texto: 'Auditoria',
          somenteGestao: true,
        icone: traco('M9 12.5l2 2 4-4M12 3l8 3.5v5c0 4.5-3.4 8.7-8 9.5-4.6-.8-8-5-8-9.5v-5L12 3z'),
      },
    ],
  },
]

const CHAVE_RECOLHIDO = 'egba_menu_recolhido'

/** Ícone de recolher/expandir: painel lateral com a seta apontando para onde vai. */
const iconeRecolher = (recolhido: boolean) =>
  traco(
    recolhido
      ? 'M4 5h16v14H4zM9 5v14M13.5 9.5l2.5 2.5-2.5 2.5'
      : 'M4 5h16v14H4zM9 5v14M16 9.5L13.5 12l2.5 2.5',
  )

export function Layout({ children }: { children: ReactNode }) {
  const { usuario, sair } = useAuth()
  const [menuAberto, setMenuAberto] = useState(false)
  const local = useLocation()

  // Lido na inicialização do estado, não num efeito: começar sempre expandido e
  // encolher depois faria a tela pular a cada carregamento de página.
  const [recolhido, setRecolhido] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_RECOLHIDO) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_RECOLHIDO, recolhido ? '1' : '0')
    } catch {
      // Navegador com armazenamento bloqueado: a preferência vale só na sessão.
    }
  }, [recolhido])

  // Ctrl/⌘+B — a convenção de VS Code, Slack e Notion. Quem procura o atalho
  // tenta esse antes de procurar o botão.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent): void {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'b') return
      const alvo = e.target as HTMLElement | null
      if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable)) {
        return
      }
      e.preventDefault()
      setRecolhido((r) => !r)
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [])

  const { data: eleitoral } = useQuery({
    queryKey: ['eleitoral'],
    queryFn: () => api.get<EstadoEleitoral>('/compliance/eleitoral'),
    staleTime: 5 * 60_000,
  })

  const admin = podeAdministrar(usuario?.papel)
  const gestao = podeGerirSistema(usuario?.papel)
  const grupos = GRUPOS.map((g) => ({
    ...g,
    itens: g.itens.filter(
      (i) =>
        (!i.somenteAdmin || admin) &&
        (!i.somenteGestao || gestao) &&
        (!i.vertical || temVertical(usuario, i.vertical)),
    ),
  })).filter((g) => g.itens.length > 0)

  // Casa também as rotas filhas: `/mercado/escola/:id` pertence a "Mercado de
  // escolas". Comparar por igualdade fazia o cabeçalho da ficha dizer "Painel".
  const itens = GRUPOS.flatMap((g) => g.itens)
  const atual =
    itens.find((i) => i.para === local.pathname) ??
    itens
      .filter((i) => i.para !== '/' && local.pathname.startsWith(`${i.para}/`))
      .sort((a, b) => b.para.length - a.para.length)[0]
  const iniciais = (usuario?.nome ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()

  // Recolher só existe a partir de `lg`. Abaixo disso a barra já é uma gaveta
  // que some inteira, e uma régua de ícones fixa comeria a largura do celular.
  const estilo = {
    '--largura-menu': recolhido ? '72px' : '248px',
    '--largura-conteudo': recolhido ? '1560px' : '1152px',
  } as CSSProperties

  return (
    <div className="min-h-dvh bg-[color:var(--color-superficie)]" style={estilo}>
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-marca-600 focus:px-4 focus:py-2 focus:text-sm focus:text-white"
      >
        Pular para o conteúdo
      </a>

      {/* ---------------------------------------------------------- Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-[color:var(--color-borda)] bg-white transition-[transform,width] duration-200 lg:w-[var(--largura-menu)] lg:translate-x-0 ${
          menuAberto ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          className={`flex h-[60px] items-center ${recolhido ? 'lg:justify-center lg:px-0' : ''} px-5`}
        >
          {/* Recolhido, a marca vira só o wordmark: "Diário / Oficial" em duas
              linhas não cabe em 72 px e sairia cortado. A gaveta do celular
              continua com a marca inteira — lá a largura é sempre 248 px. */}
          <span className={recolhido ? 'block lg:hidden' : 'block'}>
            <MarcaProduto />
          </span>
          <span className={recolhido ? 'hidden lg:block' : 'hidden'}>
            <MarcaEgba altura={22} />
          </span>
        </div>

        <nav
          className={`flex-1 space-y-6 overflow-x-hidden overflow-y-auto py-2 ${
            recolhido ? 'lg:px-2' : ''
          } px-3`}
          aria-label="Navegação principal"
        >
          {grupos.map((grupo, i) => (
            <div key={grupo.titulo ?? `grupo-${i}`}>
              {grupo.titulo && (
                <>
                  <p
                    className={`mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-slate-400 uppercase ${
                      recolhido ? 'lg:hidden' : ''
                    }`}
                  >
                    {grupo.titulo}
                  </p>
                  {/* Recolhido, o título do grupo vira um traço: a palavra não
                      caberia, mas a separação entre "jobs" é o que a barra
                      agrupada existe para dar. */}
                  {recolhido && (
                    <span
                      className="mx-auto mb-1.5 hidden h-px w-6 bg-[color:var(--color-borda)] lg:block"
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
              <ul className="space-y-0.5">
                {grupo.itens.map((item) => (
                  <li key={item.para}>
                    <NavLink
                      to={item.para}
                      onClick={() => setMenuAberto(false)}
                      title={recolhido ? item.texto : undefined}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                          recolhido ? 'lg:justify-center lg:px-0' : ''
                        } ${
                          isActive
                            ? 'bg-marca-50 font-medium text-marca-700'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* Barra de acento: marca o item ativo sem depender só
                              de cor, que é o que quem não distingue azul precisa. */}
                          {isActive && (
                            <span
                              className={`absolute top-1.5 bottom-1.5 w-[3px] rounded-r bg-marca-600 ${
                                recolhido ? 'lg:-left-2' : ''
                              } -left-3`}
                              aria-hidden="true"
                            />
                          )}
                          {item.icone}
                          {/* O texto continua no DOM quando recolhido: sem ele o
                              link fica sem nome acessível e o leitor de tela
                              anuncia "link" e mais nada. */}
                          <span className={recolhido ? 'lg:sr-only' : ''}>{item.texto}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className={`border-t border-[color:var(--color-borda)] p-3 ${recolhido ? 'lg:px-2' : ''}`}>
          <NavLink
            to="/perfil"
            onClick={() => setMenuAberto(false)}
            title={recolhido ? usuario?.nome : undefined}
            className={`flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-slate-50 ${
              recolhido ? 'lg:justify-center lg:px-0' : ''
            }`}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-marca-600 text-xs font-semibold text-white">
              {iniciais || '—'}
            </span>
            <div className={`min-w-0 flex-1 ${recolhido ? 'lg:hidden' : ''}`}>
              <p className="truncate text-sm font-medium text-slate-900">{usuario?.nome}</p>
              <p className="truncate text-xs text-slate-500">
                {NOMES_PAPEL[usuario?.papel ?? ''] ?? usuario?.papel}
              </p>
            </div>
          </NavLink>
          <button
            type="button"
            onClick={() => void sair()}
            title={recolhido ? 'Sair' : undefined}
            className={`mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 ${
              recolhido ? 'lg:justify-center lg:px-0' : ''
            }`}
          >
            {traco('M15 17l4-5-4-5M19 12H10M13 4H6a2 2 0 00-2 2v12a2 2 0 002 2h7')}
            <span className={recolhido ? 'lg:sr-only' : ''}>Sair</span>
          </button>
        </div>
      </aside>

      {menuAberto && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/30 lg:hidden"
          onClick={() => setMenuAberto(false)}
          aria-hidden="true"
        />
      )}

      {/* ------------------------------------------------------------ Corpo */}
      <div className="transition-[padding] duration-200 lg:pl-[var(--largura-menu)]">
        <header className="sticky top-0 z-20 flex h-[60px] items-center gap-3 border-b border-[color:var(--color-borda)] bg-white/85 px-4 backdrop-blur-md sm:px-6">
          <button
            type="button"
            onClick={() => setMenuAberto(true)}
            aria-label="Abrir menu"
            className="-ml-1 rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 lg:hidden"
          >
            {traco('M4 7h16M4 12h16M4 17h16')}
          </button>

          {/* No cabeçalho, e não na borda da barra: aqui o botão não muda de
              lugar quando a barra encolhe, então quem recolheu sabe onde
              clicar para voltar. */}
          <button
            type="button"
            onClick={() => setRecolhido((r) => !r)}
            aria-expanded={!recolhido}
            aria-label={recolhido ? 'Expandir menu' : 'Recolher menu'}
            title={`${recolhido ? 'Expandir' : 'Recolher'} menu · Ctrl+B`}
            className="-ml-1 hidden rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 lg:inline-flex"
          >
            {iconeRecolher(recolhido)}
          </button>

          <h1 className="text-[15px] font-semibold text-slate-900">
            {atual?.texto ?? 'Painel'}
          </h1>
        </header>

        {/* Gate desligado à mão dentro do período vedado. Vermelho e antes de
            tudo: quem desligou pode não ser quem vai clicar em disparar. */}
        {eleitoral?.gateDesligadoNaJanela && (
          <div role="alert" className="border-b border-red-300 bg-[color:var(--color-perigo-fundo)]">
            <div className="mx-auto flex max-w-[var(--largura-conteudo)] flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-[13px] text-red-900 sm:px-6 lg:px-8">
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0" aria-hidden="true">
                <path d="M8.5 2.7c.6-1.1 2.3-1.1 2.9 0l6.4 11.6c.6 1-.2 2.2-1.4 2.2H3.6c-1.2 0-2-1.2-1.4-2.2L8.5 2.7zM10 7a.8.8 0 00-.8.8v3a.8.8 0 001.6 0v-3A.8.8 0 0010 7zm0 7.5a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
              <strong className="font-semibold">Gate eleitoral desligado à mão</strong>
              <span>
                O calendário ainda está no período vedado ({eleitoral.fundamentoLegal}), mas o modo
                foi posto em <strong>off</strong> nas configurações. Peça institucional e
                promocional vai sair.
              </span>
              {podeAdministrar(usuario?.papel) && (
                <NavLink to="/configuracoes" className="font-medium underline">
                  revisar
                </NavLink>
              )}
            </div>
          </div>
        )}

        {eleitoral?.janelaAtiva && (
          <div
            role="status"
            className="border-b border-amber-200/70 bg-[color:var(--color-atencao-fundo)]"
          >
            <div className="mx-auto flex max-w-[var(--largura-conteudo)] flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-[13px] text-amber-900 sm:px-6 lg:px-8">
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0" aria-hidden="true">
                <path d="M8.5 2.7c.6-1.1 2.3-1.1 2.9 0l6.4 11.6c.6 1-.2 2.2-1.4 2.2H3.6c-1.2 0-2-1.2-1.4-2.2L8.5 2.7zM10 7a.8.8 0 00-.8.8v3a.8.8 0 001.6 0v-3A.8.8 0 0010 7zm0 7.5a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
              <strong className="font-semibold">Período eleitoral</strong>
              <span className="text-amber-800">
                {formatarDataCurta(eleitoral.janelaInicio)} a{' '}
                {formatarDataCurta(eleitoral.janelaFim)} — campanhas institucionais e
                promocionais bloqueadas ({eleitoral.fundamentoLegal}).
              </span>
              {eleitoral.excecaoMercadoHabilitada && (
                <span className="rounded bg-amber-200/80 px-1.5 py-0.5 text-[11px] font-medium">
                  exceção de mercado ativa
                </span>
              )}
            </div>
          </div>
        )}

        <main id="conteudo" className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto max-w-[var(--largura-conteudo)] transition-[max-width] duration-200">{children}</div>
        </main>
      </div>
    </div>
  )
}
